import { AppLocale } from '@app/i18n/locales';

/**
 * Maps browser locale codes to our supported app locales
 * @param browserLocale Browser locale (e.g., 'es-MX', 'es-419', 'en-US', 'fr-CA')
 * @returns App locale or null if not supported
 */
const mapBrowserLocaleToAppLocale = (browserLocale: string): AppLocale | null => {
  const locale = browserLocale.toLowerCase();

  // Spanish variants (Mexico, Spain, Argentina, etc.) → Latin American Spanish
  if (locale.startsWith('es')) {
    return AppLocale['es-419'];
  }

  // French variants → French
  if (locale.startsWith('fr')) {
    return AppLocale.fr;
  }

  // English variants (US, UK, Canada, etc.) → English
  if (locale.startsWith('en')) {
    return AppLocale.en;
  }

  return null;
};

/**
 * Detects the user's preferred locale from browser settings
 * @returns The best matching app locale
 */
export const detectBrowserLocale = (): AppLocale => {
  // Check if we're in browser environment
  if (typeof navigator === 'undefined') {
    return AppLocale.en;
  }

  // Get user's language preferences (ordered by preference)
  const browserLanguages = navigator.languages || [navigator.language];

  // Try each language in order of preference
  for (const browserLang of browserLanguages) {
    const mappedLocale = mapBrowserLocaleToAppLocale(browserLang);

    if (mappedLocale) {
      return mappedLocale;
    }
  }

  return AppLocale.en;
};
